'use client';

import { useTransition } from 'react';
import { AppButton } from '@/components/design/forms';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from '@/i18n/navigation';
import { useRegistrationMaxStep } from '@/hooks/useRegistrationMaxStep';
import { useActivityStore } from '@/state/stores/activityStore';
import { getNextRegistrationFlowPath } from '@/lib/registrationFlowNavigation';

interface ForwardFlowButtonProps {
  label?: string;
}

export function ForwardFlowButton({ label }: ForwardFlowButtonProps) {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('strings.flowNav');
  const maxStep = useRegistrationMaxStep();
  const orders = useActivityStore((s) => s.orders);
  const latestOrderId = orders.length > 0 ? orders[orders.length - 1].id : null;
  const [isPending, startTransition] = useTransition();

  const nextPath = getNextRegistrationFlowPath(pathname, maxStep, latestOrderId);

  if (!nextPath) return null;

  return (
    <AppButton
      type="button"
      disabled={isPending}
      onClick={() => {
        if (isPending) return;
        startTransition(() => {
          router.push(nextPath);
        });
      }}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm text-fest-subtle-text hover:text-fest-heading transition-colors group disabled:opacity-50 disabled:pointer-events-none"
    >
      {isPending ? (
        <Loader2 className="w-4 h-4 shrink-0 animate-spin" aria-hidden />
      ) : null}
      <span>{label ?? t('forward')}</span>
      {!isPending ? (
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      ) : null}
    </AppButton>
  );
}
